import { useState } from "react";
import { AlertTriangle, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { knowledgeProvidersService } from "@/services/knowledgeProvidersService";
import {
  applyRefreshedSchema,
  buildToolPreview,
  checkModelFitsCollection,
  expectedVectorSize,
} from "@/lib/knowledgeProviders";
import {
  DEFAULT_VOYAGE_RERANK_MODEL,
  VOYAGE_EMBEDDING_MODELS,
  VOYAGE_MODEL_DIMENSIONS,
  VOYAGE_RERANK_MODELS,
  type KnowledgeQdrantCollection,
  type VoyageEmbeddingModel,
  type VoyageRerankModel,
} from "@/types/knowledgeProviders";
import CollectionTestSearch from "./CollectionTestSearch";

interface Props {
  collection: KnowledgeQdrantCollection;
  organizationId?: string;
  otherToolNames: string[];
  onChange: (next: KnowledgeQdrantCollection | null) => void;
}

export default function QdrantCollectionCard({ collection: c, organizationId, otherToolNames, onChange }: Props) {
  const [refreshing, setRefreshing] = useState(false);
  const patch = (p: Partial<KnowledgeQdrantCollection>) => onChange({ ...c, ...p });

  const namedVectors = Object.keys(c.schema.named_vectors ?? {});
  const size = expectedVectorSize(c.schema, c.vector_name);
  const fit = checkModelFitsCollection(c.embedding_model, size);
  const toolNameClash = otherToolNames.includes(c.tool_name);
  const toolNameInvalid = !/^[a-z0-9_]{1,64}$/.test(c.tool_name);
  const preview = buildToolPreview(c);

  const refresh = async () => {
    if (!organizationId) return;
    setRefreshing(true);
    try {
      const { schema } = await knowledgeProvidersService.introspect(organizationId, c.collection);
      onChange(applyRefreshedSchema(c, schema));
    } catch (e) {
      toast({
        title: "Could not refresh the schema",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  const toggleReturned = (name: string, on: boolean) =>
    patch({ returned_fields: on ? [...c.returned_fields, name] : c.returned_fields.filter((f) => f !== name) });

  return (
    <div className="rounded-lg border p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium">{c.collection}</span>
          <Badge variant="secondary">{c.schema.points_count} points</Badge>
          <Badge variant="outline">
            {size ?? "?"} dims, {c.schema.distance}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={c.enabled} onCheckedChange={(enabled) => patch({ enabled })} />
          <Button type="button" variant="ghost" size="icon" onClick={() => onChange(null)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Embedding model */}
      <div className="space-y-2 rounded-md bg-muted/40 p-3">
        <Label>Embedding model</Label>
        <p className="text-xs text-muted-foreground">
          Must be the model this collection was embedded with.
        </p>
        <div className="flex flex-wrap gap-2">
          <Select
            value={c.embedding_model}
            onValueChange={(m) => patch({ embedding_model: m as VoyageEmbeddingModel })}
          >
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VOYAGE_EMBEDDING_MODELS.map((m) => (
                <SelectItem key={m} value={m}>
                  {m} ({[...VOYAGE_MODEL_DIMENSIONS[m]].sort((a, b) => a - b).join("/")})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {namedVectors.length > 0 && (
            <Select value={c.vector_name ?? ""} onValueChange={(v) => patch({ vector_name: v })}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Vector name" />
              </SelectTrigger>
              <SelectContent>
                {namedVectors.map((n) => (
                  <SelectItem key={n} value={n}>
                    {n} ({c.schema.named_vectors![n]})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {!fit.ok && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{fit.reason}</AlertDescription>
          </Alert>
        )}
      </div>

      {/* Tool */}
      <div className="space-y-2">
        <Label>Tool name</Label>
        <Input
          value={c.tool_name}
          onChange={(e) => patch({ tool_name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_").slice(0, 64) })}
        />
        {(toolNameClash || toolNameInvalid) && (
          <p className="text-xs text-destructive">
            {toolNameClash ? "Another collection already uses this tool name." : "Use a-z, 0-9 and _ (max 64)."}
          </p>
        )}
        <Label>Description (what the assistant reads)</Label>
        <Textarea rows={4} value={c.description} onChange={(e) => patch({ description: e.target.value })} />
      </div>

      {/* Search settings */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Max results</Label>
          <Input
            type="number"
            min={1}
            max={20}
            value={c.max_results}
            onChange={(e) => patch({ max_results: Math.min(20, Math.max(1, Number(e.target.value) || 1)) })}
          />
        </div>
        <div className="space-y-1">
          <Label>Score threshold (optional)</Label>
          <Input
            type="number"
            step="0.01"
            value={c.score_threshold ?? ""}
            onChange={(e) => patch({ score_threshold: e.target.value === "" ? null : Number(e.target.value) })}
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Switch
          checked={c.rerank}
          onCheckedChange={(rerank) =>
            patch({ rerank, rerank_model: c.rerank_model ?? DEFAULT_VOYAGE_RERANK_MODEL })
          }
        />
        <Label>Rerank results with Voyage</Label>
      </div>
      {c.rerank && (
        <div className="space-y-1">
          <Label>Rerank model</Label>
          <Select
            value={c.rerank_model ?? DEFAULT_VOYAGE_RERANK_MODEL}
            onValueChange={(m) => patch({ rerank_model: m as VoyageRerankModel })}
          >
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VOYAGE_RERANK_MODELS.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Fields */}
      <div className="space-y-2">
        <Label>Returned fields</Label>
        <p className="text-xs text-muted-foreground">Sent to the assistant. None checked sends every field.</p>
        <div className="grid grid-cols-2 gap-1">
          {c.schema.payload_fields.map((f) => (
            <label key={f.name} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={c.returned_fields.includes(f.name)}
                onCheckedChange={(on) => toggleReturned(f.name, on === true)}
              />
              {f.name}
              <span className="text-xs text-muted-foreground">{f.type}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="space-y-1">
        <Label>Filterable fields</Label>
        <p className="text-xs text-muted-foreground">
          Only fields with a Qdrant payload index. Add an index in Qdrant, then refresh the schema.
        </p>
        <div className="flex flex-wrap gap-1">
          {c.filterable_fields.length === 0 && <span className="text-sm text-muted-foreground">None</span>}
          {c.filterable_fields.map((f) => (
            <Badge key={f.name} variant="outline">
              {f.name} ({f.type})
            </Badge>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          Schema captured {new Date(c.schema.captured_at).toLocaleString()}
        </span>
        <Button type="button" variant="outline" size="sm" disabled={refreshing} onClick={refresh}>
          {refreshing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Refresh schema
        </Button>
      </div>

      <CollectionTestSearch collection={c} organizationId={organizationId} />

      <div className="space-y-1">
        <Label>Tool preview</Label>
        <pre className="rounded-md bg-muted p-3 text-xs overflow-x-auto">{JSON.stringify(preview, null, 2)}</pre>
      </div>
    </div>
  );
}
